// Resizable ticket rail (Amendment 2026-08-22, sixth, item 1): the drag
// handle between the menu grid and the ticket rail lets the bartender
// widen the rail to read full drink/liquor names, or narrow it back down
// to the product's own existing default width. Pointer Events (not
// separate mouse and touch listener pairs) unify mouse-drag and
// touch-drag in one code path, matching this product's touch-first
// mandate (CLAUDE.md: every action completable by touch alone). This
// module never wires a keyboard handler at all, and index.html's handle
// element is a plain, non-focusable <div> (no tabindex, no button/
// separator role) for that same reason: the item's own instruction is "no
// keyboard path needed but must not trap focus," and an element that is
// never part of the tab order can never trap focus.
//
// Width persists in localStorage, the same mechanism theme.ts documents
// itself as using for the device's chosen theme (see that file's own
// header comment) BEFORE Amendment 2026-08-22 (third) moved theme choice
// to the user's own server-side profile so it follows a person across
// terminals. Rail width deliberately does NOT follow that same move: it is
// a physical-terminal ergonomic setting (how much of THIS particular
// screen a bartender wants devoted to the ticket versus the menu grid),
// not a personal preference that should travel with a login to a
// different terminal that may have a different screen size entirely. A
// server-synced width would follow a bartender to a smaller screen and
// immediately be wrong there. Device-local storage is the deliberately
// correct home for it, even though it makes this the one remaining
// device-local setting after theme's own move away from that pattern.

export const RAIL_WIDTH_DEFAULT_PX = 440; // UIFIX5: was 400, so a liquor name and Prepared fit without cutting the name

// Amendment 2026-08-22 (sixth): "The current default width stays the
// default," and the drag only ever widens the rail "or narrow[s] it
// back" - never below where it already sits today. The min clamp is
// therefore the existing default itself (styles.css's own former fixed
// `.ticket-rail { width: 400px }`), not some smaller, never-shipped
// compact width.
export const RAIL_WIDTH_MIN_PX = RAIL_WIDTH_DEFAULT_PX;

// Chosen and live-verified (Claude Browser pane, both supported tablet
// widths: 1280x800 and 1024x768) against the menu grid's own
// `.tile-grid { grid-template-columns: repeat(auto-fill, minmax(190px,
// 1fr)) }` with its space-3 (12px) gap and `.menu-scroll`'s space-5 (20px)
// side padding. 1024x768 is the tighter of the two real constraints (not
// 1280, which has far more room to spare): at that width, a 560px rail
// plus this module's own 24px handle leaves the menu pane roughly 440px
// of usable content width after padding, comfortably above the ~392px
// two 190px tiles plus one gap require. A larger max would start losing
// the second tile column at 1024x768 before it ever threatened the
// looser 1280x800 case the item's own wording names.
export const RAIL_WIDTH_MAX_PX = 560;

const RAIL_WIDTH_STORAGE_KEY = "bms-ui-rail-width-px";

export function clampRailWidthPx(px: number): number {
  if (!Number.isFinite(px)) {
    return RAIL_WIDTH_DEFAULT_PX;
  }
  return Math.min(RAIL_WIDTH_MAX_PX, Math.max(RAIL_WIDTH_MIN_PX, Math.round(px)));
}

export function loadStoredRailWidthPx(): number {
  const stored = window.localStorage.getItem(RAIL_WIDTH_STORAGE_KEY);
  if (stored === null) {
    return RAIL_WIDTH_DEFAULT_PX;
  }
  const parsed = Number.parseFloat(stored);
  return clampRailWidthPx(parsed);
}

export function saveRailWidthPx(px: number): void {
  window.localStorage.setItem(RAIL_WIDTH_STORAGE_KEY, String(clampRailWidthPx(px)));
}

/** Wires the drag handle's pointer-driven resize onto `railEl`, applying
 * live width changes via `railEl.style.width` and persisting the final
 * value on pointerup/pointercancel. `handleEl` is the drag affordance
 * itself, a DOM sibling of `railEl` (see index.html's ".body" children),
 * never a child of it, so the handle's own hit area is never affected by
 * the rail's changing width.
 *
 * Applies the stored (or default) width immediately, so a fresh load
 * restores whatever this device last set. Unlike the theme (UX21,
 * DEC-117: reset on every device, never device-persisted), a device's
 * rail width is deliberately still remembered locally: it is a layout
 * preference, not a product default the owner wants uniform everywhere. */
export function initRailResize(handleEl: HTMLElement, railEl: HTMLElement): void {
  railEl.style.width = `${loadStoredRailWidthPx()}px`;

  let dragStartClientX = 0;
  let dragStartWidthPx = 0;
  let activePointerId: number | undefined;

  const onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== activePointerId) {
      return;
    }
    const deltaX = event.clientX - dragStartClientX;
    // DOM/visual order is menu-pane, then this handle, then the rail: the
    // handle sits to the rail's LEFT, so dragging left (a negative deltaX)
    // widens the rail and dragging right narrows it - width moves opposite
    // to clientX's own delta.
    railEl.style.width = `${clampRailWidthPx(dragStartWidthPx - deltaX)}px`;
  };

  const endDrag = (event: PointerEvent): void => {
    if (event.pointerId !== activePointerId) {
      return;
    }
    activePointerId = undefined;
    handleEl.classList.remove("dragging");
    handleEl.removeEventListener("pointermove", onPointerMove);
    handleEl.removeEventListener("pointerup", endDrag);
    handleEl.removeEventListener("pointercancel", endDrag);
    saveRailWidthPx(railEl.getBoundingClientRect().width);
  };

  handleEl.addEventListener("pointerdown", (event) => {
    // Left button only for mouse (button 0); touch and pen report button
    // 0 on contact too, so this also admits both of those.
    if (event.button > 0) {
      return;
    }
    activePointerId = event.pointerId;
    dragStartClientX = event.clientX;
    dragStartWidthPx = railEl.getBoundingClientRect().width;
    handleEl.classList.add("dragging");
    // Pointer capture keeps pointermove/pointerup delivered to handleEl
    // even once the pointer has moved off it mid-drag (routine once a
    // fast drag outruns a 24px-wide element), which is what makes this
    // work as one continuous drag rather than losing tracking.
    handleEl.setPointerCapture(event.pointerId);
    handleEl.addEventListener("pointermove", onPointerMove);
    handleEl.addEventListener("pointerup", endDrag);
    handleEl.addEventListener("pointercancel", endDrag);
    event.preventDefault();
  });
}
